import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { api, isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import { GENERAL_INQUIRIES_EMAIL } from "lib/fortifyContact";
import swtAlert from "lib/sweetAlert";
import {
  isEmail,
  required,
  validateForm as runFormValidation,
  validateName,
  validatePhone,
  validateText,
} from "lib/validation";
import { selectAuthUser, selectIsAuthenticated } from "store/reducer";
import { getIndustryRecordId, selectIndustries } from "store/reducer/configData";

const INITIAL_VALUES = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  company: "",
  industry: "",
  targetRaiseAmount: "",
  description: "",
};

const TARGET_RAISE_OPTIONS = [
  { value: "50k-100k", label: "$50,000 - $100,000" },
  { value: "100k-250k", label: "$100,000 - $250,000" },
  { value: "250k-500k", label: "$250,000 - $500,000" },
  { value: "500k-1m", label: "$500,000 - $1,000,000" },
  { value: "1m+", label: "$1,000,000+" },
];

function getIndustryLabel(industries, industryId) {
  const match = industries.find((ind) => getIndustryRecordId(ind) === industryId);
  if (!match) return industryId;
  return match.name ?? match.title ?? match.label ?? match.slug ?? industryId;
}

function getTargetRaiseLabel(value) {
  return TARGET_RAISE_OPTIONS.find((opt) => opt.value === value)?.label ?? value;
}

function buildRaiseContactPayload(formData, industries) {
  const payload = {
    firstName: formData.firstName.trim(),
    lastName: formData.lastName.trim(),
    email: formData.email.trim(),
    subject: `Company Registration — ${formData.company.trim()}`,
    message: [
      "Company registration application submitted via the Raise page.",
      "",
      `Company: ${formData.company.trim()}`,
      `Industry: ${getIndustryLabel(industries, formData.industry)}`,
      `Target raise amount: ${getTargetRaiseLabel(formData.targetRaiseAmount)}`,
      "",
      "About the company:",
      formData.description.trim(),
    ].join("\n"),
  };

  const phoneTrimmed = formData.phone.trim();
  if (phoneTrimmed) payload.phone = phoneTrimmed;
  return payload;
}

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} className="text-xs text-destructive">
      {message}
    </p>
  );
}

export function RaiseRegisterSection() {
  const user = useSelector(selectAuthUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const industries = useSelector(selectIndustries);
  const emailLocked = isAuthenticated && Boolean(user?.email);

  const [formData, setFormData] = useState(INITIAL_VALUES);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const industryOptions = useMemo(
    () =>
      industries
        .map((ind) => {
          const id = getIndustryRecordId(ind);
          if (!id) return null;
          const label = ind.name ?? ind.title ?? ind.label ?? ind.slug ?? id;
          return { id, label };
        })
        .filter(Boolean),
    [industries]
  );

  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setFormData((prev) => ({ ...prev, email: user.email }));
    }
  }, [isAuthenticated, user?.email]);

  const validate = (values = formData) =>
    runFormValidation(values, {
      firstName: (v) => validateName(v, { label: "First name" }),
      lastName: (v) => validateName(v, { label: "Last name" }),
      email: [required("Email"), isEmail],
      phone: (v) => (v?.trim() ? validatePhone(v, { label: "Phone" }) : null),
      company: (v) => validateText(v, { label: "Company name", min: 2, max: 120 }),
      industry: required("Industry"),
      targetRaiseAmount: required("Target raise amount"),
      description: (v) =>
        validateText(v, { label: "Company description", min: 10, max: 2000 }),
    });

  const resetForm = () => {
    setFormData(emailLocked ? { ...INITIAL_VALUES, email: user.email } : INITIAL_VALUES);
    setTouched({});
    setErrors({});
  };

  const clearFieldError = (name) => {
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "email" && emailLocked) return;
    const nextValue = name === "phone" ? value.replace(/\D+/g, "").slice(0, 15) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    clearFieldError(name);
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldErrors = validate();
    setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] || "" }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name);
  };

  const handleSelectBlur = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldErrors = validate();
    setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] || "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const newErrors = validate();
    setErrors(newErrors);
    setTouched(
      Object.keys(INITIAL_VALUES).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {})
    );
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const resp = await api.post(
        API_ENDPOINTS.contact,
        buildRaiseContactPayload(formData, industries),
        { auth: false }
      );

      if (!isSuccessResponse(resp)) {
        await swtAlert.error(
          "Application not sent",
          resp?.message ||
            `Something went wrong. Please email us directly at ${GENERAL_INQUIRIES_EMAIL}.`
        );
        return;
      }

      await swtAlert.success(
        "Application submitted",
        resp?.message ||
          "Thanks for registering — our team will contact you within 2 business days."
      );
      resetForm();
    } catch (err) {
      await swtAlert.error(
        "Application not sent",
        err?.message ||
          `Something went wrong. Please email us directly at ${GENERAL_INQUIRIES_EMAIL}.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const showError = (field) => Boolean(touched[field] && errors[field]);

  return (
    <section className="py-16 sm:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
        <div className="mx-auto max-w-2xl">
          <Card className="border shadow-sm">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Register Your Company</CardTitle>
              <CardDescription>
                Fill out the form below and our team will contact you within 2 business
                days.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-6" noValidate onSubmit={handleSubmit}>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="raise-firstName">
                      First name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="raise-firstName"
                      name="firstName"
                      autoComplete="given-name"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-invalid={showError("firstName")}
                      aria-describedby={
                        showError("firstName") ? "raise-firstName-error" : undefined
                      }
                    />
                    <FieldError
                      id="raise-firstName-error"
                      message={showError("firstName") ? errors.firstName : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="raise-lastName">
                      Last name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="raise-lastName"
                      name="lastName"
                      autoComplete="family-name"
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-invalid={showError("lastName")}
                      aria-describedby={
                        showError("lastName") ? "raise-lastName-error" : undefined
                      }
                    />
                    <FieldError
                      id="raise-lastName-error"
                      message={showError("lastName") ? errors.lastName : ""}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="raise-email">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="raise-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    readOnly={emailLocked}
                    disabled={emailLocked}
                    className={emailLocked ? "bg-muted/50" : undefined}
                    aria-invalid={showError("email")}
                    aria-describedby={showError("email") ? "raise-email-error" : undefined}
                  />
                  <FieldError
                    id="raise-email-error"
                    message={showError("email") ? errors.email : ""}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="raise-phone">
                    Phone number <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    id="raise-phone"
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    pattern="[0-9]*"
                    maxLength={15}
                    placeholder="5551234567"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={showError("phone")}
                    aria-describedby={showError("phone") ? "raise-phone-error" : undefined}
                  />
                  <FieldError
                    id="raise-phone-error"
                    message={showError("phone") ? errors.phone : ""}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="raise-company">
                    Company name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="raise-company"
                    name="company"
                    placeholder="Your Company Inc."
                    value={formData.company}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={showError("company")}
                    aria-describedby={
                      showError("company") ? "raise-company-error" : undefined
                    }
                  />
                  <FieldError
                    id="raise-company-error"
                    message={showError("company") ? errors.company : ""}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="raise-industry">
                    Industry <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => handleSelectChange("industry", value)}
                    onOpenChange={(open) => {
                      if (!open) handleSelectBlur("industry");
                    }}
                  >
                    <SelectTrigger
                      id="raise-industry"
                      className="w-full"
                      aria-invalid={showError("industry")}
                      aria-describedby={
                        showError("industry") ? "raise-industry-error" : undefined
                      }
                    >
                      <SelectValue
                        placeholder={
                          industryOptions.length > 0
                            ? "Select an industry"
                            : "No industries available"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {industryOptions.map((ind) => (
                        <SelectItem key={ind.id} value={ind.id}>
                          {ind.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError
                    id="raise-industry-error"
                    message={showError("industry") ? errors.industry : ""}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="raise-amount">
                    Target raise amount <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.targetRaiseAmount}
                    onValueChange={(value) =>
                      handleSelectChange("targetRaiseAmount", value)
                    }
                    onOpenChange={(open) => {
                      if (!open) handleSelectBlur("targetRaiseAmount");
                    }}
                  >
                    <SelectTrigger
                      id="raise-amount"
                      className="w-full"
                      aria-invalid={showError("targetRaiseAmount")}
                      aria-describedby={
                        showError("targetRaiseAmount") ? "raise-amount-error" : undefined
                      }
                    >
                      <SelectValue placeholder="Select target amount" />
                    </SelectTrigger>
                    <SelectContent>
                      {TARGET_RAISE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError
                    id="raise-amount-error"
                    message={
                      showError("targetRaiseAmount") ? errors.targetRaiseAmount : ""
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="raise-description">
                    Tell us about your company <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="raise-description"
                    name="description"
                    placeholder="Describe your business, what stage you're at, and what you plan to use the funds for..."
                    rows={5}
                    value={formData.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={showError("description")}
                    aria-describedby={
                      showError("description")
                        ? "raise-description-error"
                        : "raise-description-counter"
                    }
                  />
                  <div className="flex items-start justify-between gap-3 text-xs">
                    {showError("description") ? (
                      <FieldError
                        id="raise-description-error"
                        message={errors.description}
                      />
                    ) : (
                      <span id="raise-description-counter" className="text-muted-foreground">
                        {formData.description.trim().length}/2000 characters
                      </span>
                    )}
                  </div>
                </div>

                <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                      Submitting...
                    </>
                  ) : (
                    "Submit Application"
                  )}
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  By submitting, you agree to our{" "}
                  <Link to="/terms" className="font-medium text-accent hover:underline">
                    Terms of Use
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="font-medium text-accent hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
